import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { ArrowRight, Sparks, NavArrowRight } from "iconoir-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SiteFooter from "@/components/SiteFooter";
import { PLANS, formatPrice } from "@/constants/pricing";

const PageNav = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  return (
    <nav className="sticky top-0 z-40 backdrop-blur-md bg-bone/85 border-b border-border" data-testid="public-nav">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
          <div className="w-3 h-3 bg-intl" />
          <span className="font-app font-bold tracking-tight text-base">AI Visibility OS</span>
        </Link>
        <div className="hidden md:flex items-center gap-8 text-sm font-app">
          <a href="#features" className="hover:text-intl transition-colors">{t("nav.platform")}</a>
          <a href="#how" className="hover:text-intl transition-colors">{t("nav.how")}</a>
          <a href="#pricing" className="hover:text-intl transition-colors">{t("nav.pricing")}</a>
          <a href="#faq" className="hover:text-intl transition-colors">{t("nav.faq")}</a>
        </div>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {user ? (
            <Link to="/dashboard" data-testid="nav-go-dashboard" className="px-4 py-2 bg-obsidian text-bone font-app text-sm font-bold uppercase tracking-wider hover:bg-intl transition-colors">{t("nav.dashboard")}</Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-app hover:text-intl">{t("nav.login")}</Link>
              <Link to="/register" data-testid="nav-register" className="px-4 py-2 bg-obsidian text-bone font-app text-sm font-bold uppercase tracking-wider hover:bg-intl transition-colors">{t("nav.getScore")}</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

const HeroNumber = ({ label, value, suffix }) => (
  <div className="border-l border-border pl-6">
    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-2 font-mono text-3xl">{value}<span className="text-intl">{suffix}</span></div>
  </div>
);

const FeatureRow = ({ idx, title, body }) => (
  <div className="grid md:grid-cols-12 gap-6 py-10 border-t border-border">
    <div className="md:col-span-2 font-mono text-xs uppercase tracking-widest text-muted-foreground">{`/${String(idx).padStart(2, "0")}`}</div>
    <h3 className="md:col-span-4 font-marketing text-3xl tracking-tight leading-none">{title}</h3>
    <p className="md:col-span-6 text-base leading-relaxed text-muted-foreground">{body}</p>
  </div>
);

const FAQItem = ({ q, a }) => (
  <details className="group border-t border-border py-6 cursor-pointer">
    <summary className="flex justify-between items-center list-none">
      <span className="font-app font-medium text-lg">{q}</span>
      <NavArrowRight className="transition-transform group-open:rotate-90" />
    </summary>
    <p className="mt-4 text-muted-foreground leading-relaxed">{a}</p>
  </details>
);

export default function Landing() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || "en";

  const onCheckScore = (e) => {
    e.preventDefault();
    const url = new FormData(e.currentTarget).get("url");
    navigate(`/register?url=${encodeURIComponent(url || "")}`);
  };

  return (
    <div className="min-h-screen grain font-body" data-testid="landing-page">
      <PageNav />

      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24 relative">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-3">
              <span className="w-8 h-px bg-intl" /> {t("hero.badge")}
            </div>
            <h1 data-testid="hero-headline" className="mt-6 font-marketing text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.9]">
              {t("hero.title_a")} <span className="italic">{t("hero.title_ready")}</span><br />
              {t("hero.title_b")} <span className="text-intl">{t("hero.title_ai")}</span>?
            </h1>
            <p className="mt-8 text-lg max-w-2xl text-muted-foreground leading-relaxed">{t("hero.subtitle")}</p>

            <form onSubmit={onCheckScore} data-testid="hero-form" className="mt-10 flex flex-col sm:flex-row gap-2 max-w-xl">
              <input
                name="url"
                data-testid="hero-url-input"
                required
                type="text"
                placeholder={t("hero.placeholder")}
                className="flex-1 bg-card border border-border px-4 py-4 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-intl"
              />
              <button
                type="submit"
                data-testid="hero-cta-button"
                className="px-6 py-4 bg-obsidian text-bone font-app font-bold uppercase tracking-wider text-sm hover:bg-intl transition-colors flex items-center justify-center gap-2"
              >
                {t("hero.cta")} <ArrowRight strokeWidth={2.2} />
              </button>
            </form>
            <div className="mt-4 font-mono text-xs text-muted-foreground">{t("hero.disclaimer")}</div>
          </div>

          <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">
            <div className="relative aspect-square overflow-hidden border border-border bg-obsidian">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/9ea2ca5e-1f36-4ba6-aef8-7221951ae105/images/bdc24ded41b7732d5c80fc9f06181ecd6256b84784a0cfbb18467f1da972d861.png"
                alt="AI radar scan"
                className="w-full h-full object-cover opacity-90"
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-obsidian/95 to-transparent">
                <div className="font-mono text-[10px] uppercase tracking-widest text-bone/70">{t("hero.liveScan")}</div>
                <div className="font-mono text-bone text-sm">{t("hero.sitesAnalysed")}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <HeroNumber label={t("hero.avgScore")} value="48" suffix="/100" />
              <HeroNumber label={t("hero.gap")} value="38" suffix="pts" />
            </div>
          </div>
        </div>

        {/* logos / engines */}
        <div className="mt-20 border-y border-border py-6 overflow-hidden">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">{t("engines.optimisedFor")}</div>
          <div className="flex flex-wrap gap-x-12 gap-y-3 font-app text-lg">
            {["ChatGPT", "Google AI Overview", "Gemini", "Claude", "Perplexity", "Copilot", "Meta AI"].map((n) => (
              <span key={n} className="opacity-80">{n}</span>
            ))}
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section id="features" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-5">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("problem.tag")}</div>
            <h2 className="mt-4 font-marketing text-4xl sm:text-5xl leading-none tracking-tight">
              {t("problem.title_seo")}<br /><span className="text-intl">{t("problem.title_aeo")}</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-7 space-y-6 text-lg leading-relaxed">
            <p>{t("problem.p1")}</p>
            <p>{t("problem.p2")}</p>
          </div>
        </div>

        <div className="mt-20">
          <FeatureRow idx={1} title={t("features.f1_title")} body={t("features.f1_body")} />
          <FeatureRow idx={2} title={t("features.f2_title")} body={t("features.f2_body")} />
          <FeatureRow idx={3} title={t("features.f3_title")} body={t("features.f3_body")} />
          <FeatureRow idx={4} title={t("features.f4_title")} body={t("features.f4_body")} />
          <FeatureRow idx={5} title={t("features.f5_title")} body={t("features.f5_body")} />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-obsidian text-bone py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-12 gap-6 mb-16">
            <div className="col-span-12 lg:col-span-5">
              <div className="font-mono text-xs uppercase tracking-widest text-bone/60">{t("how.tag")}</div>
              <h2 className="mt-4 font-marketing text-4xl sm:text-5xl tracking-tight leading-none">{t("how.title")}</h2>
            </div>
            <p className="col-span-12 lg:col-span-7 text-bone/70 text-lg leading-relaxed">{t("how.subtitle")}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-bone/20 border border-bone/20">
            {[
              { n: "01", t: t("how.s1_title"), d: t("how.s1_body") },
              { n: "02", t: t("how.s2_title"), d: t("how.s2_body") },
              { n: "03", t: t("how.s3_title"), d: t("how.s3_body") },
            ].map((s) => (
              <div key={s.n} className="bg-obsidian p-10">
                <div className="font-mono text-xs text-intl">{s.n}</div>
                <h3 className="mt-4 font-app text-2xl">{s.t}</h3>
                <p className="mt-4 text-bone/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLE TERMINAL */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("terminal.tag")}</div>
            <h2 className="mt-4 font-marketing text-4xl sm:text-5xl tracking-tight leading-none">{t("terminal.title_a")} <span className="text-intl">{t("terminal.title_b")}</span></h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">{t("terminal.body")}</p>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <div className="terminal p-6 text-sm leading-relaxed">
              <div className="text-bone/50 mb-3 font-mono text-xs">// generated_fix.jsonld</div>
              <pre className="whitespace-pre-wrap">
{`{
  `}<span className="key">"@context"</span>{`: `}<span className="str">"https://schema.org"</span>{`,
  `}<span className="key">"@type"</span>{`: `}<span className="str">"FAQPage"</span>{`,
  `}<span className="key">"mainEntity"</span>{`: [
    {
      `}<span className="key">"@type"</span>{`: `}<span className="str">"Question"</span>{`,
      `}<span className="key">"name"</span>{`: `}<span className="str">"Do you ship internationally?"</span>{`,
      `}<span className="key">"acceptedAnswer"</span>{`: {
        `}<span className="key">"@type"</span>{`: `}<span className="str">"Answer"</span>{`,
        `}<span className="key">"text"</span>{`: `}<span className="str">"Yes — to 42 countries, in 3 business days."</span>{`
      }
    }
  ]
}`}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-5">
            <img src="https://images.unsplash.com/photo-1590650153855-d9e808231d41?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Business owner" className="w-full aspect-[4/5] object-cover border border-border" />
          </div>
          <div className="md:col-span-7">
            <Sparks className="text-intl" width={32} height={32} strokeWidth={1.5} />
            <p className="mt-6 font-marketing text-3xl sm:text-4xl leading-tight">{t("testimonial.quote")}</p>
            <div className="mt-8 font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("testimonial.author")}</div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-5">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("pricing.tag")}</div>
            <h2 className="mt-4 font-marketing text-4xl sm:text-5xl tracking-tight leading-none">{t("pricing.title")}</h2>
          </div>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-px bg-border border border-border">
          {PLANS.map((p) => {
            const points = t(p.pointsKey, { returnObjects: true });
            return (
              <div key={p.key} className={`bg-background p-10 ${p.featured ? "outline outline-2 -outline-offset-2 outline-intl relative" : ""}`} data-testid={`landing-plan-${p.key}`}>
                {p.featured && <div className="absolute top-3 right-3 font-mono text-[10px] uppercase tracking-widest text-intl">{t("pricing.most_popular")}</div>}
                <div className="font-app text-xl">{t(p.nameKey)}</div>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-marketing text-5xl" data-testid={`landing-price-${p.key}`}>{formatPrice(p, lang)}</span>
                  <span className="text-muted-foreground text-sm">{p.usd === 0 ? t("pricing.per_forever") : t("pricing.per_month")}</span>
                </div>
                <ul className="mt-8 space-y-3 text-sm">
                  {Array.isArray(points) && points.map((pt) => (
                    <li key={pt} className="flex gap-3"><span className="text-intl">→</span>{pt}</li>
                  ))}
                </ul>
                <Link to="/register" className={`mt-10 block text-center py-3 font-app uppercase text-xs font-bold tracking-widest ${p.featured ? "bg-intl text-white hover:bg-obsidian" : "border border-obsidian hover:bg-obsidian hover:text-bone"} transition-colors`}>
                  {t("pricing.start_free")}
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-4">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("faq.tag")}</div>
            <h2 className="mt-4 font-marketing text-4xl sm:text-5xl tracking-tight leading-none">{t("faq.title")}</h2>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <FAQItem q={t("faq.q1")} a={t("faq.a1")} />
            <FAQItem q={t("faq.q2")} a={t("faq.a2")} />
            <FAQItem q={t("faq.q3")} a={t("faq.a3")} />
            <FAQItem q={t("faq.q4")} a={t("faq.a4")} />
            <FAQItem q={t("faq.q5")} a={t("faq.a5")} />
            <FAQItem q={t("faq.q6")} a={t("faq.a6")} />
            <FAQItem q={t("faq.q7")} a={t("faq.a7")} />
            <FAQItem q={t("faq.q8")} a={t("faq.a8")} />
            <FAQItem q={t("faq.q9")} a={t("faq.a9")} />
            <FAQItem q={t("faq.q10")} a={t("faq.a10")} />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-obsidian text-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-12 gap-6 items-end">
          <h2 className="col-span-12 lg:col-span-8 font-marketing text-4xl sm:text-6xl tracking-tighter leading-none">
            {t("finalCta.title_a")} <span className="text-intl">{t("finalCta.title_score")}</span> {t("finalCta.title_b")}
          </h2>
          <div className="col-span-12 lg:col-span-4 lg:text-right">
            <Link to="/register" data-testid="footer-cta" className="inline-flex items-center gap-3 px-8 py-5 bg-intl text-white font-app font-bold uppercase tracking-wider text-sm hover:bg-bone hover:text-obsidian transition-colors">
              {t("finalCta.button")} <ArrowRight />
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
